import React from 'react';
import { DynamicForm } from '../components/SelForm/DynamicForm';

// Example 1: Simple Two-Column Form
const SimpleTwoColumnExample = () => {
  const fields = [
    {
      id: 'firstName',
      name: 'First Name',
      type: 'text',
      colSpan: 6, // Takes half width (6 of 12 columns)
      validation: { required: 'First name is required' }
    },
    {
      id: 'lastName',
      name: 'Last Name',
      type: 'text',
      colSpan: 6, // Takes half width
      validation: { required: 'Last name is required' }
    },
    {
      id: 'email',
      name: 'Email Address',
      type: 'email',
      colSpan: 'full', // Takes full width
      validation: {
        required: 'Email is required',
        pattern: {
          value: /^\S+@\S+$/i,
          message: 'Invalid email address'
        }
      }
    },
    {
      id: 'phone',
      name: 'Phone Number',
      type: 'tel',
      colSpan: 6
    },
    {
      id: 'dateOfBirth',
      name: 'Date of Birth',
      type: 'date',
      colSpan: 6
    }
  ];

  return (
    <DynamicForm
      fields={fields}
      formTitle="Simple Two Column Form"
      layout={{
        type: 'grid',
        columns: 12,
        gap: 4
      }}
      onSubmit={(data) => console.log(data)}
    />
  );
};

// Example 2: Responsive Multi-Column Form
const ResponsiveFormExample = () => {
  const fields = [
    {
      id: 'fullName',
      name: 'Full Name',
      type: 'text',
      colSpan: 'full', // Always full width
      validation: { required: 'Name is required' }
    },
    {
      id: 'email',
      name: 'Email',
      type: 'email',
      colSpan: {
        default: 12, // Full width on mobile
        md: 6,       // Half width on medium screens
        lg: 4        // Third width on large screens
      }
    },
    {
      id: 'phone',
      name: 'Phone',
      type: 'tel',
      colSpan: {
        default: 12,
        md: 6,
        lg: 4
      }
    },
    {
      id: 'company',
      name: 'Company',
      type: 'text',
      colSpan: {
        default: 12,
        md: 12,
        lg: 4
      }
    }
  ];

  return (
    <DynamicForm
      fields={fields}
      formTitle="Responsive Form"
      formDescription="This form adapts to different screen sizes"
      layout={{
        type: 'grid',
        columns: 12,
        gap: 4,
        responsive: {
          sm: 1,  // 1 column on small screens
          md: 2,  // 2 columns on medium screens
          lg: 3   // 3 columns on large screens
        }
      }}
      onSubmit={(data) => console.log(data)}
    />
  );
};

// Example 3: Complex Layout with Mixed Column Spans
const ComplexLayoutExample = () => {
  const fields = [
    // Header section - full width
    {
      id: 'title',
      name: 'Title',
      type: 'select',
      colSpan: 2,
      options: [
        { value: 'mr', label: 'Mr.' },
        { value: 'mrs', label: 'Mrs.' },
        { value: 'ms', label: 'Ms.' },
        { value: 'dr', label: 'Dr.' }
      ]
    },
    {
      id: 'firstName',
      name: 'First Name',
      type: 'text',
      colSpan: 5,
      validation: { required: 'Required' }
    },
    {
      id: 'lastName',
      name: 'Last Name',
      type: 'text',
      colSpan: 5,
      validation: { required: 'Required' }
    },
    
    // Contact section - mixed widths
    {
      id: 'email',
      name: 'Email Address',
      type: 'email',
      colSpan: 8,
      validation: { required: 'Email is required' }
    },
    {
      id: 'phoneType',
      name: 'Phone Type',
      type: 'select',
      colSpan: 4,
      options: [
        { value: 'mobile', label: 'Mobile' },
        { value: 'home', label: 'Home' },
        { value: 'work', label: 'Work' }
      ]
    },
    {
      id: 'phoneNumber',
      name: 'Phone Number',
      type: 'tel',
      colSpan: 8
    },
    {
      id: 'extension',
      name: 'Ext.',
      type: 'text',
      colSpan: 4,
      placeholder: 'Optional'
    },
    
    // Address section
    {
      id: 'streetAddress',
      name: 'Street Address',
      type: 'text',
      colSpan: 'full'
    },
    {
      id: 'apartment',
      name: 'Apt/Suite',
      type: 'text',
      colSpan: 3
    },
    {
      id: 'city',
      name: 'City',
      type: 'text',
      colSpan: 3
    },
    {
      id: 'state',
      name: 'State',
      type: 'select',
      colSpan: 3,
      options: [
        { value: 'ca', label: 'California' },
        { value: 'tx', label: 'Texas' },
        { value: 'ny', label: 'New York' },
        // ... more states
      ]
    },
    {
      id: 'zipCode',
      name: 'ZIP Code',
      type: 'text',
      colSpan: 3
    },
    
    // Full width fields
    {
      id: 'comments',
      name: 'Comments',
      type: 'textarea',
      colSpan: 'full',
      rows: 4
    },
    {
      id: 'subscribe',
      name: 'Newsletter',
      type: 'checkbox',
      colSpan: 'full',
      checkboxLabel: 'Subscribe to our newsletter'
    }
  ];

  return (
    <DynamicForm
      fields={fields}
      formTitle="Complex Layout Form"
      formDescription="Demonstrates various column spanning options"
      layout={{
        type: 'grid',
        columns: 12,
        gap: 4
      }}
      showProgress={true}
      onSubmit={(data) => console.log(data)}
    />
  );
};

// Example 4: Using Column Start for Custom Positioning
const CustomPositioningExample = () => {
  const fields = [
    {
      id: 'logo',
      name: 'Company Logo',
      type: 'file',
      colSpan: 3,
      colStart: 1
    },
    {
      id: 'companyName',
      name: 'Company Name',
      type: 'text',
      colSpan: 6,
      colStart: 5 // Skip one column after logo
    },
    {
      id: 'industry',
      name: 'Industry',
      type: 'select',
      colSpan: 3,
      colStart: 1,
      options: [
        { value: 'tech', label: 'Technology' },
        { value: 'finance', label: 'Finance' },
        { value: 'healthcare', label: 'Healthcare' }
      ]
    },
    {
      id: 'employees',
      name: 'Number of Employees',
      type: 'range',
      colSpan: 4,
      min: 1,
      max: 1000,
      hint: 'Drag to select range'
    },
    {
      id: 'founded',
      name: 'Year Founded',
      type: 'number',
      colSpan: 2,
      min: 1900,
      max: 2024
    }
  ];

  return (
    <DynamicForm
      fields={fields}
      formTitle="Custom Positioning Example"
      layout={{
        type: 'grid',
        columns: 12,
        gap: 6
      }}
      onSubmit={(data) => console.log(data)}
    />
  );
};

// Example 5: Group Fields with Internal Layouts
const GroupedFieldsExample = () => {
  const fields = [
    {
      id: 'personalInfo',
      type: 'group',
      title: 'Personal Information',
      fields: [
        {
          id: 'firstName',
          name: 'First Name',
          type: 'text',
          colSpan: 6
        },
        {
          id: 'lastName',
          name: 'Last Name',
          type: 'text',
          colSpan: 6
        },
        {
          id: 'dateOfBirth',
          name: 'Date of Birth',
          type: 'date',
          colSpan: 4
        },
        {
          id: 'gender',
          name: 'Gender',
          type: 'radio',
          colSpan: 8,
          options: [
            { value: 'male', label: 'Male' },
            { value: 'female', label: 'Female' },
            { value: 'other', label: 'Other' },
            { value: 'prefer-not', label: 'Prefer not to say' }
          ]
        }
      ]
    },
    {
      id: 'addressInfo',
      type: 'group',
      title: 'Address Information',
      fields: [
        {
          id: 'street',
          name: 'Street Address',
          type: 'text',
          colSpan: 'full'
        },
        {
          id: 'city',
          name: 'City',
          type: 'text',
          colSpan: 4
        },
        {
          id: 'state',
          name: 'State',
          type: 'text',
          colSpan: 4
        },
        {
          id: 'zip',
          name: 'ZIP',
          type: 'text',
          colSpan: 4
        }
      ]
    }
  ];

  return (
    <DynamicForm
      fields={fields}
      formTitle="Grouped Fields Form"
      layout={{
        type: 'grid',
        columns: 12,
        gap: 4
      }}
      onSubmit={(data) => console.log(data)}
    />
  );
};

// Main App Component
function Columns() {
  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto space-y-12">
        <h1 className="text-3xl font-bold text-center mb-8">
          Multi-Column Form Examples
        </h1>
        
        {/* You can toggle between examples */}
        <ComplexLayoutExample />
        
        {/* Or render multiple examples */}
        <SimpleTwoColumnExample />
        {/* <ResponsiveFormExample /> */}
        {/* <CustomPositioningExample /> */}
        {/* <GroupedFieldsExample /> */}
      </div>
    </div>
  );
}

export default Columns;